import {
	type CircleLayerSpecification,
	type FillLayerSpecification,
	type GeoJSONSource,
	type HeatmapLayerSpecification,
	type LineLayerSpecification,
	type SymbolLayerSpecification,
	type VectorTileSource,
} from "maplibre-gl";

import type { TOperationalFeature } from "safesystem_geoserver-api";

type BBox = {
	minx: number;
	miny: number;
	maxx: number;
	maxy: number;
};

/*
	# Data/Schema heirarchy

		Product Suite: Abley SafeSystem
			Product: SafeRoads, Data Mire, FARS, etc.
				Version:
					Module: Identify, Prioritize, Curves, Accidents
						Features[]: Signs/Speeds, ...or may just be module name...

		Features are not explicitly enumerated, just listed within structure
*/

export interface IFeature extends TOperationalFeature {
	product: EProduct;
	module: TModule;
}

export enum ESafeRoadsModule {
	Identify = "Identify",
	Prioritize = "Prioritize",
}
export enum ECrashes {
	Accidents = "Accidents",
}

export const EModule = { ...ESafeRoadsModule, ...ECrashes };
export type TModule = ESafeRoadsModule | ECrashes;

export enum EProduct {
	SafeRoads = "SafeRoads",
	FARS = "FARS",
}

export type TModuleRecord = Record<TModule, IFeature[]>;
export type TVersionRecord = Record<string, TModuleRecord>;
export type TProductRecord = Record<EProduct, TVersionRecord>;

export enum EIdentify {
	Identify = "Identify",
}
export enum EPrioritize {
	Prioritize = "Prioritize",
}
export enum EAccidents {
	AccidentsHeatmap = "Accidents (heatmap)",
	AccidentsCircle = "Accidents (circle)",
	UploadedCrashes = "Uploaded crash data",
}

export type TLayerId = EIdentify | EPrioritize | EAccidents;

export type TLayer =
	| CircleLayerSpecification
	| FillLayerSpecification
	| HeatmapLayerSpecification
	| LineLayerSpecification
	| SymbolLayerSpecification;
export type TLayerPaint = TLayer["paint"];
interface LayerSearchQuery {
	/** feature properties included in matching search query */
	queryProperties: string[];
}
export interface ILayerSource extends LayerSearchQuery {
	id: TLayerId;
	zIndex: number;
	title: string;
	bbox?: BBox;
	layer: Partial<TLayer>;
	layerPaintOnSelect: TLayerPaint;
	layerPaintInitial?: TLayerPaint;
	source: Partial<VectorTileSource> & { attribution: string } | Partial<GeoJSONSource>;
	wfsRootUrl?: string;
}

export interface ISystemLayers {
	[ESafeRoadsModule.Identify]: {
		[EIdentify.Identify]: ILayerSource;
	};
	[ESafeRoadsModule.Prioritize]: {
		[EPrioritize.Prioritize]: ILayerSource;
	};
	[ECrashes.Accidents]: {
		[EAccidents.AccidentsHeatmap]: ILayerSource;
		[EAccidents.AccidentsCircle]: ILayerSource;
		[EAccidents.UploadedCrashes]?: ILayerSource;
	};
};

export interface Basemap {
	id: string;
	label: string;
	altText: string;
	image: {
		jpg: string;
		webp: string;
	};
	source: string;
}
